<script setup>
import {ref} from "vue";
import CreateTask from "./createTask.vue";

const emit = defineEmits(['completed', 'deleted', 'updated']);

const props = defineProps({
    task: {
        type: Object,
        required: true,
    }
})

const editing = ref(false)
const editingLabel = ref(false)
const playCompletionAnimation = ref(false)

const toggleCompleted = async () => {
    let timeout = 0;
    if (!props.task.is_completed) {
        playCompletionAnimation.value = true
        timeout = 1400
    }
    const res = await axios.put(route('api.task.completed', props.task.id), {
        completed: !props.task.is_completed
    })

    setTimeout(() => {
        playCompletionAnimation.value = false
        emit('completed', res.data.task)
        props.task.is_completed = !props.task.is_completed
    }, timeout)

}

const deleteTask = async () => {
    await axios.delete(route('api.task.delete', props.task.id))
    emit('deleted')
}

const onUpdated = (updatedTask) => {
    editing.value = false
    emit('updated', updatedTask)
}

</script>

<template>
    <div>
        <div v-if="! editing" class="flex space-x-3 mb-px pt-3 pb-2">
            <div class="relative">
                <button
                    class="w-5 h-5 rounded-sm border-2 flex items-center justify-center transition-all"
                    :class="[task.is_completed ? 'bg-green-600 border-green-600' : '', playCompletionAnimation ? 'animate-wiggle bg-green-600 border-green-600' : task.is_completed ? 'bg-green-600' : 'bg-white border-gray-400']"
                    @click="toggleCompleted"
                >
                    <i
                        class="fa-solid fa-check text-xs transition-opacity"
                        :class="task.is_completed ? 'opacity-100 text-white' : 'opacity-0 text-black'"
                    ></i>
                </button>
                <div
                    v-if="playCompletionAnimation"
                    class="z-10 absolute inset-0 w-5 h-5 rounded-sm border-2 flex items-center justify-center pointer-events-none bg-green-600 border-green-600"
                    :class="!task.is_completed && playCompletionAnimation ? 'animate-fall-in' : ''"
                >
                    <i
                        class="fa-solid fa-check text-xs opacity-100 text-white"
                    ></i>
                </div>
            </div>
            <div
                class="w-full transition-opacity group -mt-px"
                :class="task.is_completed && ! editingLabel ? 'opacity-50' : ''"
            >
                <div class="relative">
                    <div
                        class="relative"
                    >
                        <strong
                            class="block w-full min-h-4 cursor-text"
                        >{{ task.label }}</strong>
                    </div>
                </div>
                <div class="flex justify-start items-center space-x-2 relative">
                    <div
                        class="flex justify-start items-center space-x-2"
                    >
                        <span
                            v-if="! task.is_available && task.available_at"
                            class="text-xxs pr-1"
                            :class="task.is_available ? 'text-gray-500' : 'text-gray-400'"
                        >
                            Start {{ task.available_at.date}}  {{ task.available_at.time_display}}
                        </span>
                        <span
                            v-if="task.is_available && task.due_at"
                            class="text-xxs pr-1"
                            :class="task.is_due && !task.is_completed ? 'px-1 bg-red-500 text-white rounded-md' : 'text-gray-500'"
                        >
                                Due {{ task.due_at.date}} {{ task.due_at.time_display}}
                        </span>

                        <div
                            v-if="task.priority > 0"
                            :class="['bg-gray-200 text-gray-700', 'bg-red-500 text-white', 'bg-yellow-500 text-white', 'bg-blue-500 text-white'][task.priority]"
                            class="p-2 rounded-md text-xxs w-5 h-5 flex justify-center items-center"
                        >
                            <span>{{ task.priority }}</span>
                        </div>
                    </div>
                </div>

            </div>
            <button-transparent
                @click="editing = ! editing"
                size="sm"
            ><i class="fa-regular fa-pen text-gray-400"></i></button-transparent>
        </div>
        <div class="relative">
            <create-task
                v-if="editing === true"
                class="py-3 -mx-4 lg:-mx-6 px-4 lg:px-6 bg-gray-100"
                :task="task"
                @cancel="editing = false"
                @updated="onUpdated"
            ></create-task>
            <span
                v-if="editing === true"
                class="absolute bottom-4 left-0"
            >
                <button-with-confirmation
                    size="sm"
                    style-type="transparent"
                    confirm-type="danger"
                    confirm-button-label="Delete"
                    :abort-button-label="_mft('shared:action.cancel')"
                    @confirmed="deleteTask"
                >
                    <i class="fa-regular fa-trash text-myflowRed"></i>
                </button-with-confirmation>
            </span>
        </div>
    </div>
</template>

<style scoped>
@keyframes fall-in {
    0% {
        opacity: 0;
        transform: scale(8);
        border-radius: 50%;
    }
    75% {
        opacity: 1;
        transform: scale(0.5);
        border-radius: 0;
    }
    100% {
        opacity: 1;
        transform: scale(1);
        border-radius: 0;
    }
}

@keyframes wiggle {
    0%, 7% {
        transform: scale(1);
        border-radius: 0;
    }
    15% {
        transform: scale(1.3);
    }
    20% {
        transform: scale(1.4);
    }
    25% {
        transform: scale(1.42);
        border-radius: 16%;
    }
    30% {
        transform: scale(1.37);
    }
    35% {
        transform: scale(1.24);
    }
    45% {
        transform: scale(1.12);
        border-radius: 0;
    }
    45%, 100% {
        transform: scale(1);
    }
}

.animate-wiggle {
    animation: wiggle 0.8s 0.25s ease-out;
}

.animate-fall-in {
  animation: fall-in 0.3s ease-in;
}
</style>
